<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';

import { usePageBack } from '../composables/usePageBack';
import { useOrganizationStore } from '../stores/organization';

const props = defineProps<{ title: string, backLink?: string, link?: string }>();
const { backLink: pageBackLink } = usePageBack();
const orgStore = useOrganizationStore();

// 1:1 Angular PageTitleService: [pageTitle, organizationName, titleSuffix] joined with " – ";
// when neither page title nor organization is known, the app's htmlTitle stands alone.
function documentTitle(pageTitle: string): string {
    const { titleSuffix, htmlTitle } = window.kgrDynamicConfig;
    const suffix = titleSuffix ?? 'KigaRoo';
    const organizationName = orgStore.organization?.name ?? '';
    if (!pageTitle && !organizationName && htmlTitle) {
        return htmlTitle;
    }
    return [pageTitle, organizationName, suffix].filter(Boolean).join(' – ');
}

function updateDocumentTitle() {
    document.title = documentTitle(props.title);
}

// The organization arrives asynchronously (initApp) — re-apply once it does, like Angular's
// combineLatest over title$ + organizationTitle$.
watch([() => props.title, () => orgStore.organization?.name], updateDocumentTitle);

// Angular haelt den Back-Link in einem Singleton-Service; jede View, die ihn setzt, raeumt ihn in
// `ngOnDestroy` wieder ab, und `team.component` ruft `resetBackLink()` sogar rein defensiv. Der
// Grund ist in Vue derselbe: beim Routenwechsel montiert vue-router die NEUE Komponente, bevor die
// alte zerstoert wird — ein unbedingtes `= null` im Unmount wuerde also den gerade gesetzten Link
// der Zielroute loeschen. Deshalb raeumt jede Instanz nur ihren eigenen Wert ab.
let ownBackLink: string | null = null;

function applyBackLink(value: string | undefined) {
    if (ownBackLink !== null && pageBackLink.value === ownBackLink) {
        pageBackLink.value = null;
    }
    ownBackLink = value ?? null;
    if (ownBackLink !== null) {
        pageBackLink.value = ownBackLink;
    }
}

watch(() => props.backLink, applyBackLink);

onMounted(() => {
    updateDocumentTitle();
    applyBackLink(props.backLink);
});
onUnmounted(() => {
    document.title = documentTitle('');
    if (ownBackLink !== null && pageBackLink.value === ownBackLink) {
        pageBackLink.value = null;
    }
});
</script>

<template>
    <Teleport
        defer
        to="#kgr-pageTitle"
    >
        <RouterLink
            v-if="link"
            :to="link"
        >
            {{ title }}
        </RouterLink>
        <!-- Angular's portal template wraps the interpolation in template whitespace, so two page
             titles teleported into the same container end up separated by a space („Profil bearbeiten
             Anna Admin"). At the edges of the inline box the extra spaces collapse away, so a single
             title renders unchanged. -->
        <template v-else>
            {{ ' ' }}{{ title }}{{ ' ' }}
        </template>
    </Teleport>
</template>
